import { useEffect, useRef, useState } from 'react';

const BarList = ({ lang, selectedExercise }: any) => {
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [barHeight, setBarHeight] = useState(200);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [tooltip, setTooltip] = useState<{x: number, y: number, value: number, visible: boolean}>({
        x: 0, y: 0, value: 0, visible: false
    });

    useEffect(() => {
        const updateBarHeight = () => {
            if (containerRef.current) {
                const container = containerRef.current;
                const containerHeight = container.clientHeight;
                const actualBarHeight = containerHeight - 140;
                setBarHeight(Math.max(actualBarHeight, 50));
            }
        };
        const timeoutId = setTimeout(updateBarHeight, 100);
        updateBarHeight();
        window.addEventListener('resize', updateBarHeight);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateBarHeight);
        };
    }, []);

    useEffect(() => {
        if (selectedExercise) {
            setForceUpdate(prev => prev + 1);
        }
    }, [selectedExercise]);

    function getWeekday(dateObject: any) {
        const date = new Date(dateObject)
        return new Intl.DateTimeFormat(lang, { weekday: "short" }).format(date);
    }

    function getMonthAndDay(dateObject: any) {
        const date = new Date(dateObject)
        const formattedDate = new Intl.DateTimeFormat(lang, { month: "short", day: "2-digit" }).format(date);

        return formattedDate.replace(" ", "\u00A0");
    }

    function getYear(dateObject: any) {
        const date = new Date(dateObject)
        return new Intl.DateTimeFormat(lang, { year: "numeric" }).format(date);
    }

    function getColorOfBar(percent: any) {
        return percent >= 70
            ? "linear-gradient(to bottom, rgba(35, 207, 149, 0.7), rgba(3, 158, 107, 0.7))"
            : percent >= 30
                ? "linear-gradient(to bottom,rgba(255, 117, 39, 0.7), rgba(208, 75, 0, 0.7))"
                : "linear-gradient(to bottom,rgba(227, 80, 107, 0.3), rgba(197, 32, 62, 0.5))"
    }

    const calculatePathLength = (points: string[]) => {
        if (points.length < 2) return 0;
        
        let totalLength = 0;
        for (let i = 1; i < points.length; i++) {
            const [x1, y1] = points[i - 1].split(',').map(Number);
            const [x2, y2] = points[i].split(',').map(Number);
            const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
            totalLength += distance;
        }
        return totalLength;
    };

    const createLinePath = (data: any[], containerHeight: number) => {
        if (!data || data.length === 0) return { path: "", length: 0 };
        
        const barElements = containerRef.current?.querySelectorAll('.bar-item');
        if (!barElements || barElements.length === 0) {
            return { path: "", length: 0 };
        }
        
        if (barElements.length !== data.length) {
            return { path: "", length: 0 };
        }
        
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) {
            return { path: "", length: 0 };
        }
        
        const yPadding = 10;
        const usableHeight = containerHeight - (2 * yPadding);

        const points = data.map((timeBar, index) => {
            const percentage = Math.round(timeBar.completionPercentage)
            
            const barElement = barElements[index] as HTMLElement;
            if (!barElement) {
                return null;
            }
            
            const barRect = barElement.getBoundingClientRect();
            const x = barRect.left - containerRect.left + (barRect.width / 2);
            const y = yPadding + (usableHeight - (percentage / 100) * usableHeight);
            
            return `${x},${y}`;
        }).filter((point): point is string => point !== null);
        
        if (points.length === 0) return { path: "", length: 0 };
        
        const path = `M ${points.join(' L ')}`;
        const length = calculatePathLength(points);
        
        return { path, length };
    };

    const createLinePoints = (data: any[], containerHeight: number) => {
        if (!data || data.length === 0) return [];
        
        const barElements = containerRef.current?.querySelectorAll('.bar-item');
        if (!barElements || barElements.length === 0) return [];
        
        if (barElements.length !== data.length) return [];
        
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return [];
        
        const yPadding = 10;
        const usableHeight = containerHeight - (2 * yPadding);
        
        return data.map((timeBar, index) => {
            const percentage = Math.round(timeBar.completionPercentage)
            
            const barElement = barElements[index] as HTMLElement;
            if (!barElement) return null;
            
            const barRect = barElement.getBoundingClientRect();
            const x = barRect.left - containerRect.left + (barRect.width / 2);
            const y = yPadding + (usableHeight - (percentage / 100) * usableHeight);
            
            return { x, y, percentage };
        }).filter((point): point is { x: number; y: number; percentage: number } => point !== null);
    };

    const barList = () => {
        if (selectedExercise) {
            return selectedExercise.exerciseTimeLine.map((timeBar: any, index: number) => {
                const successPercentage = Math.round(timeBar.successPercentage)
                return (
                    <div key={index} className="flex flex-column justify-content-center align-items-center bar-item">
                        <p className="m-0 font-bold text-lg" style={{ color: "#2D3967" }}>
                            {successPercentage}</p>
                        <p className="m-0 text-sm" style={{ color: "#2D3967" }}>%</p>
                        <div style={{
                            width: "2rem",
                            height: "100%",
                            backgroundRepeat: "no-repeat",
                            background: "#E0E3EE url('/barLine.png')",
                            position: "relative",
                            borderRadius: "35px",
                            overflow: "hidden",
                        }}>
                            <div
                                style={{
                                    width: "100%",
                                    height: successPercentage + "%",
                                    background: getColorOfBar(successPercentage),
                                    position: "absolute",
                                    bottom: 0,
                                    borderRadius: "35px 35px 0 0",
                                    transition: "height 1s ease-in-out",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "start",
                                    overflow: "hidden"
                                }}
                            >
                            </div>
                        </div>
                        <p className="m-0 font-bold" style={{ color: "#9EA4B9" }}>{getWeekday(timeBar.dateWillBeDone)}</p>
                        <p className="m-0 text-sm" style={{ color: "#9EA4B9" }}>{getMonthAndDay(timeBar.dateWillBeDone)}</p>
                        <p className="m-0 text-sm" style={{ color: "#9EA4B9" }}>{getYear(timeBar.dateWillBeDone)}</p>
                    </div>
                )
            })
        }
        else {
            return <></>
        }
    }

    return (
        <div ref={containerRef} className="flex gap-6 py-4 px-2 h-full" style={{ position: "relative" }}>
            {barList()}
            {selectedExercise && selectedExercise.exerciseTimeLine && selectedExercise.exerciseTimeLine.length > 1 && (
                <>
                    <svg
                        style={{
                            position: "absolute",
                            top: "70px",
                            left: "0px",
                            width: "calc(100% + 16px)",
                            height: `${barHeight - 20}px`,
                            pointerEvents: "none",
                            zIndex: 10,
                            overflow: "visible"
                        }}
                    >
                        <defs>
                            <style>
                                {`
                                    .smooth-path {
                                        transition: d 1s ease-in-out;
                                    }
                                    
                                    .smooth-circle {
                                        transition: cx 1s ease-in-out, 
                                                   cy 1s ease-in-out;
                                    }
                                `}
                            </style>
                        </defs>
                        <path
                            d={createLinePath(selectedExercise.exerciseTimeLine, barHeight - 20).path}
                            stroke="#2D3967"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="smooth-path"
                        />
                        {createLinePoints(selectedExercise.exerciseTimeLine, barHeight - 20
                            ).map((point, index) => {                                
                                return (
                                    <g key={`point-${index}`}>
                                        <circle
                                            cx={point.x}
                                            cy={point.y}
                                            r="6"
                                            fill="white"
                                            stroke="#2D3967"
                                            strokeWidth="2"
                                            className="smooth-circle"
                                        />
                                        <circle
                                            cx={point.x}
                                            cy={point.y}
                                            r="3"
                                            fill="#2D3967"
                                            className="smooth-circle"
                                        />
                                    </g>
                                );
                            })}
                    </svg>
                    
                    {createLinePoints(selectedExercise.exerciseTimeLine, barHeight - 20).map((point, index) => (
                        <div
                            key={`tooltip-${index}`}
                            style={{
                                position: "absolute",
                                top: `${70 + point.y - 12}px`,
                                left: `${point.x - 12}px`,
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                zIndex: 20,
                                cursor: "pointer"
                            }}
                            onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltip({
                                    x: rect.left + 12,
                                    y: rect.top - 8,
                                    value: point.percentage,
                                    visible: true
                                });
                            }}
                            onMouseLeave={() => {
                                setTooltip(prev => ({ ...prev, visible: false }));
                            }}
                        />
                    ))}
                    
                    {tooltip.visible && (
                        <div
                            style={{
                                position: "fixed",
                                top: `${tooltip.y}px`,
                                left: `${tooltip.x}px`,
                                background: "#2D3967",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "bold",
                                zIndex: 1000,
                                transform: "translate(-50%, -100%)",
                                pointerEvents: "none",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                            }}
                        >
                            {tooltip.value}%
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default BarList
